import { FilterAlt } from '@mui/icons-material'
import {
  Autocomplete,
  type AutocompleteRenderInputParams,
  Button,
  Popover,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import type { PlaceNode } from '@ulysses-inc/harami_api_client'
import type React from 'react'
import { useCallback, useEffect, useState } from 'react'
import { shallowEqual, useSelector } from 'react-redux'
import { splitAccessiblePlaceNode } from 'src/exShared/libraries/filterDropDown/place'
import {
  flattenNodes,
  mergedPlaceNodeIds,
} from 'src/exShared/util/place/placeNode'
import { splitLoginPlaceUUIDs } from 'src/exShared/util/place/splitLoginPlace'
import { useListFilter } from 'src/hooks/filter/useListFilter'
import type { RootState } from 'src/state/store'
import {
  useCurrentEndDateState,
  useCurrentStartDateState,
  useFilteredPlaceGroupNodeIdsState,
  useFilteredPlaceNodeIdsState,
  useRequestScheduleData,
} from './shared.hooks'

interface OwnProps {
  isSchedule: boolean
}

const PlaceFilterDropDown: React.FC<OwnProps> = ({ isSchedule }) => {
  const type = isSchedule ? 'schedule' : 'deviance'

  const places = useSelector(
    (state: RootState) => state.placesState.places.places,
    shallowEqual,
  )
  const placeNodes = useSelector(
    (state: RootState) => flattenNodes(state.placesState.placeGroups.nodes),
    shallowEqual,
  )

  const [startDate] = useCurrentStartDateState(type)
  const [endDate] = useCurrentEndDateState(type)
  const [filteredPlaceIds, setFilteredPlaceIds] =
    useFilteredPlaceNodeIdsState(type)
  const [filteredPlaceGroupIds, setFilteredPlaceGroupIds] =
    useFilteredPlaceGroupNodeIdsState(type)

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const isOpen = anchorEl !== null

  const [placeGroupNodeIds, setPlaceGroupNodeIds] = useState<string[]>(
    filteredPlaceGroupIds,
  )
  const [placeNodeIds, setPlaceNodeIds] = useState<string[]>(filteredPlaceIds)

  const requestScheduleData = useRequestScheduleData()
  const { filters, setListFilter } = useListFilter()

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- useEffect内でのsetState呼び出しは既存設計のため、リファクタリング時に修正する
    setPlaceGroupNodeIds(filteredPlaceGroupIds)
    setPlaceNodeIds(filteredPlaceIds)
  }, [filteredPlaceGroupIds, filteredPlaceIds])

  const { loginPlaceGroupIds, loginPlaceIds } = splitLoginPlaceUUIDs()

  const isFilterOn = !!placeGroupNodeIds.length || !!placeNodeIds.length

  const changeFilter = useCallback(
    (
      requestPlaceNodeIds: string[],
      selectedPlaceNodeIds: string[],
      selectedPlaceGroupNodeIds: string[],
    ) => {
      if (!(startDate && endDate)) {
        return
      }

      setListFilter({
        ...(filters ?? {}),
        [isSchedule ? 'unstartedSchedules' : 'invalidCountSchedules']: {
          filter: {
            placeNodeIds: selectedPlaceNodeIds,
            placeGroupNodeIds: selectedPlaceGroupNodeIds,
          },
        },
      })
      setFilteredPlaceIds(selectedPlaceNodeIds)
      setFilteredPlaceGroupIds(selectedPlaceGroupNodeIds)

      requestScheduleData(isSchedule, requestPlaceNodeIds, startDate, endDate)
    },
    [
      isSchedule,
      filters,
      setListFilter,
      setFilteredPlaceIds,
      setFilteredPlaceGroupIds,
      requestScheduleData,
      startDate,
      endDate,
    ],
  )

  const getDefaultPlaceNodeIDs = useCallback(() => {
    return mergedPlaceNodeIds(
      placeNodes,
      loginPlaceIds || [],
      loginPlaceGroupIds || [],
    )
  }, [placeNodes, loginPlaceIds, loginPlaceGroupIds])

  const executeFilter = useCallback(
    (targetPlaceNodeIds: string[], targetPlaceGroupNodeIds: string[]) => {
      const nodeIds = mergedPlaceNodeIds(
        placeNodes,
        targetPlaceNodeIds,
        targetPlaceGroupNodeIds,
      )
      const requestPlaceNodeIds =
        nodeIds.length > 0 ? nodeIds : getDefaultPlaceNodeIDs()
      const selectedPlaceNodeIds =
        nodeIds.length > 0 ? targetPlaceNodeIds : loginPlaceIds || []
      const selectedPlaceGroupNodeIds =
        nodeIds.length > 0 ? targetPlaceGroupNodeIds : loginPlaceGroupIds || []

      changeFilter(
        requestPlaceNodeIds,
        selectedPlaceNodeIds,
        selectedPlaceGroupNodeIds,
      )
    },
    [
      placeNodes,
      getDefaultPlaceNodeIDs,
      loginPlaceIds,
      loginPlaceGroupIds,
      changeFilter,
    ],
  )

  const resetFilter = useCallback(() => {
    setPlaceNodeIds([])
    setPlaceGroupNodeIds([])
    executeFilter([], [])
    setAnchorEl(null)
  }, [executeFilter, setPlaceNodeIds, setPlaceGroupNodeIds])

  const handleSelectChange =
    (type: 'place' | 'placeGroup') =>
    (selectedIds: string[] = []) => {
      const isPlace = type === 'place'
      if (isPlace) {
        setPlaceNodeIds(selectedIds)
      } else {
        setPlaceGroupNodeIds(selectedIds)
      }
    }

  const applyFilter = useCallback(() => {
    executeFilter(placeNodeIds, placeGroupNodeIds)
    setAnchorEl(null)
  }, [executeFilter, placeNodeIds, placeGroupNodeIds])

  const renderSelect = (type: 'place' | 'placeGroup') => {
    const isPlace = type === 'place'

    const accessiblePlaceAndPlaceGroupNodes = splitAccessiblePlaceNode(
      placeNodes,
      places,
    )[isPlace ? 'accessiblePlaces' : 'accessiblePlaceGroups']

    return (
      <Stack spacing={1}>
        <Typography variant="subtitle1">
          現場{isPlace ? '' : 'グループ'}名
        </Typography>
        <Autocomplete<PlaceNode, true, false, false>
          multiple
          size="small"
          options={accessiblePlaceAndPlaceGroupNodes}
          isOptionEqualToValue={(option, value) => option.uuid === value.uuid}
          getOptionLabel={placeNode =>
            (isPlace ? placeNode.place : placeNode.placeGroup)?.name || ''
          }
          value={
            isPlace
              ? accessiblePlaceAndPlaceGroupNodes.filter(node =>
                  placeNodeIds.includes(node.uuid),
                )
              : accessiblePlaceAndPlaceGroupNodes.filter(node =>
                  placeGroupNodeIds.includes(node.uuid),
                )
          }
          onChange={(_, selectedPlaceNodes) => {
            const uuid = selectedPlaceNodes.map(placeNode => placeNode.uuid)
            handleSelectChange(type)(uuid)
          }}
          slotProps={{
            chip: {
              size: 'small',
              variant: 'pale',
            },
          }}
          renderInput={(params: AutocompleteRenderInputParams) => (
            <TextField {...params} />
          )}
        />
      </Stack>
    )
  }

  const hoverArea = (
    <Stack spacing={2}>
      {renderSelect('placeGroup')}
      {renderSelect('place')}
      <Stack spacing={2} direction="row" justifyContent="flex-end">
        <Button onClick={resetFilter} variant="outlined" color="primary">
          リセット
        </Button>
        <Button variant="contained" color="primary" onClick={applyFilter}>
          適用
        </Button>
      </Stack>
    </Stack>
  )

  return (
    <>
      <Button
        color={isFilterOn ? 'filtered' : 'secondary'}
        variant="outlined"
        startIcon={<FilterAlt />}
        onClick={({ currentTarget }) => {
          setAnchorEl(currentTarget)
        }}
      >
        現場を絞り込む: {isFilterOn ? 'ON' : 'OFF'}
      </Button>
      <Popover
        anchorEl={anchorEl}
        open={isOpen}
        onClose={() => {
          setAnchorEl(null)
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        slotProps={{
          paper: {
            sx: {
              padding: 2,
              width: '100%',
              maxWidth: 400,
            },
          },
        }}
      >
        {hoverArea}
      </Popover>
    </>
  )
}

export default PlaceFilterDropDown
